import { useEffect, useMemo, useState, type FormEvent } from 'react';
import type { CreateTicketTypeInput, TicketType } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { CheckboxField, SelectField, TextField } from '../../components/ui/Field.js';
import { FormActions, useSaveMode } from '../../components/ui/FormActions.js';
import { Modal } from '../../components/ui/Modal.js';
import { useServiceCategories } from '../service-categories/api.js';
import { useBillingClasses } from '../billing-classes/api.js';
import {
  useCreateTicketType,
  useDeleteTicketType,
  useTicketTypeGroups,
  useUpdateTicketType,
} from './api.js';

interface TicketTypeFormModalProps {
  isOpen: boolean;
  ticketType: TicketType | null;
  onClose: () => void;
}

interface FormState {
  name: string;
  groupId: string;
  defaultServiceCategoryId: string;
  defaultBillingClassId: string;
  invoiceSku: string;
  qboClass: string;
  isActive: boolean;
}

const EMPTY_FORM: FormState = {
  name: '',
  groupId: '',
  defaultServiceCategoryId: '',
  defaultBillingClassId: '',
  invoiceSku: '',
  qboClass: '',
  isActive: true,
};

function toFormState(ticketType: TicketType | null): FormState {
  if (!ticketType) {
    return EMPTY_FORM;
  }
  return {
    name: ticketType.name,
    groupId: ticketType.groupId,
    defaultServiceCategoryId: ticketType.defaultServiceCategoryId,
    defaultBillingClassId: ticketType.defaultBillingClassId,
    invoiceSku: ticketType.invoiceSku ?? '',
    qboClass: ticketType.qboClass ?? '',
    isActive: ticketType.isActive,
  };
}

function fieldErrorsOf(error: unknown): Record<string, string> {
  if (error instanceof ApiError && error.envelope?.error.details) {
    const result: Record<string, string> = {};
    for (const detail of error.envelope.error.details) {
      result[detail.path] = detail.message;
    }
    return result;
  }
  return {};
}

export function TicketTypeFormModal({
  isOpen,
  ticketType: ticketTypeProp,
  onClose,
}: TicketTypeFormModalProps): JSX.Element {
  const [ticketType, setTicketType] = useState<TicketType | null>(ticketTypeProp);
  const isEdit = ticketType !== null;
  const saveMode = useSaveMode();
  const [form, setForm] = useState<FormState>(() => toFormState(ticketType));
  const [confirmOpen, setConfirmOpen] = useState(false);

  const createMutation = useCreateTicketType();
  const updateMutation = useUpdateTicketType();
  const deleteMutation = useDeleteTicketType();
  const mutation = isEdit ? updateMutation : createMutation;

  const resetCreate = createMutation.reset;
  const resetUpdate = updateMutation.reset;
  const resetSaveMode = saveMode.reset;

  const groupsQuery = useTicketTypeGroups({ page: 1, pageSize: 200, isActive: true });
  const categoriesQuery = useServiceCategories({ page: 1, pageSize: 200, isActive: true });
  const billingClassesQuery = useBillingClasses({ page: 1, pageSize: 200, isActive: true });

  const groupOptions = useMemo(
    () => (groupsQuery.data?.data ?? []).map((g) => ({ value: g.id, label: g.name })),
    [groupsQuery.data],
  );
  const categoryOptions = useMemo(
    () => (categoriesQuery.data?.data ?? []).map((c) => ({ value: c.id, label: c.name })),
    [categoriesQuery.data],
  );
  const billingClassOptions = useMemo(
    () => (billingClassesQuery.data?.data ?? []).map((b) => ({ value: b.id, label: b.name })),
    [billingClassesQuery.data],
  );

  useEffect(() => {
    if (isOpen) {
      setTicketType(ticketTypeProp);
    }
  }, [isOpen, ticketTypeProp]);

  useEffect(() => {
    if (isOpen) {
      setForm(toFormState(ticketType));
      setConfirmOpen(false);
      resetSaveMode();
      resetCreate();
      resetUpdate();
    }
  }, [isOpen, ticketType, resetSaveMode, resetCreate, resetUpdate]);

  const fieldErrors = fieldErrorsOf(mutation.error);
  const generalError =
    mutation.error instanceof ApiError && !mutation.error.envelope?.error.details?.length
      ? mutation.error.message
      : mutation.isError && !(mutation.error instanceof ApiError)
        ? 'Failed to save ticket type.'
        : null;

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const payload: CreateTicketTypeInput = {
      name: form.name.trim(),
      groupId: form.groupId,
      defaultServiceCategoryId: form.defaultServiceCategoryId,
      defaultBillingClassId: form.defaultBillingClassId,
      invoiceSku: form.invoiceSku.trim() ? form.invoiceSku.trim() : null,
      qboClass: form.qboClass.trim() ? form.qboClass.trim() : null,
      isActive: form.isActive,
    };
    if (isEdit) {
      updateMutation.mutate(
        { id: ticketType.id, input: payload },
        { onSuccess: () => saveMode.closeAfterSaveRef.current && onClose() },
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: (created) => {
          if (saveMode.closeAfterSaveRef.current) {
            onClose();
          } else {
            setTicketType(created);
          }
        },
      });
    }
  };

  const handleDelete = (): void => {
    if (!ticketType) {
      return;
    }
    deleteMutation.mutate(ticketType.id, {
      onSuccess: () => {
        setConfirmOpen(false);
        onClose();
      },
    });
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={isEdit ? 'Edit ticket type' : 'New ticket type'}
        footer={
          <FormActions
            formId="ticket-type-form"
            saveMode={saveMode}
            isPending={mutation.isPending}
            onCancel={onClose}
            leading={
              isEdit ? (
                <Button
                  variant="ghost"
                  className="mr-auto text-rose-600 hover:bg-rose-50"
                  onClick={() => setConfirmOpen(true)}
                  disabled={mutation.isPending || deleteMutation.isPending}
                >
                  Delete
                </Button>
              ) : null
            }
          />
        }
      >
        <form id="ticket-type-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
          {generalError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {generalError}
            </div>
          ) : null}

          <TextField
            label="Name"
            required
            value={form.name}
            maxLength={100}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            error={fieldErrors.name}
          />

          <SelectField
            label="Group"
            required
            value={form.groupId}
            placeholder={groupsQuery.isLoading ? 'Loading…' : 'Select a group'}
            options={groupOptions}
            onChange={(e) => setForm((f) => ({ ...f, groupId: e.target.value }))}
            error={fieldErrors.groupId}
          />

          <div className="grid grid-cols-2 gap-4">
            <SelectField
              label="Default service category"
              required
              value={form.defaultServiceCategoryId}
              placeholder={categoriesQuery.isLoading ? 'Loading…' : 'Select a category'}
              options={categoryOptions}
              onChange={(e) =>
                setForm((f) => ({ ...f, defaultServiceCategoryId: e.target.value }))
              }
              error={fieldErrors.defaultServiceCategoryId}
            />
            <SelectField
              label="Default billing class"
              required
              value={form.defaultBillingClassId}
              placeholder={billingClassesQuery.isLoading ? 'Loading…' : 'Select a billing class'}
              options={billingClassOptions}
              onChange={(e) => setForm((f) => ({ ...f, defaultBillingClassId: e.target.value }))}
              error={fieldErrors.defaultBillingClassId}
            />
          </div>

          <TextField
            label="Invoice SKU"
            value={form.invoiceSku}
            maxLength={64}
            placeholder="QuickBooks item name for ticket time"
            onChange={(e) => setForm((f) => ({ ...f, invoiceSku: e.target.value }))}
            error={fieldErrors.invoiceSku}
          />

          <TextField
            label="QBO class"
            value={form.qboClass}
            maxLength={256}
            placeholder="QuickBooks class for ticket time"
            onChange={(e) => setForm((f) => ({ ...f, qboClass: e.target.value }))}
            error={fieldErrors.qboClass}
          />

          <CheckboxField
            label="Active"
            checked={form.isActive}
            onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
          />
        </form>
      </Modal>
      <ConfirmDialog
        isOpen={confirmOpen}
        title="Delete ticket type"
        message={`Delete "${ticketType?.name ?? ''}"? This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        isConfirming={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}
